/**
 * Hero Section Enhancement Script
 * Handles video loading, fallbacks, and special functionality
 */

// Prevent redeclaration if script loads multiple times
if (!window.HeroSectionDefined) {
  window.HeroSectionDefined = true;

  class HeroSection extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
    }

    connectedCallback() {
      if (!this.initialized) {
        this.init();
        this.initialized = true;
      }
    }

    init() {
      // Wait for content to be fully rendered
      setTimeout(() => {
        this.video = this.querySelector('.hero__video');
        this.fallbackImage = this.querySelector('.hero__image--fallback');
        this.images = this.querySelectorAll('.hero__image') || [];
        
        // Debug logging
        if (window.location.search.includes('debug=true')) {
          console.log('Hero section initialized:', {
            video: this.video,
            images: this.images.length,
            fallback: this.fallbackImage
          });
        }
        
        this.setupVideoHandling();
        this.setupImageLoading();
        this.setupIntersectionObserver();
      }, 100);
    }

    setupVideoHandling() {
      if (!this.video) return;

      // Handle direct video elements (not iframes)
      if (this.video.tagName === 'VIDEO') {
        this.video.addEventListener('loadeddata', () => {
          this.video.setAttribute('data-loaded', 'true');
          this.hideVideoFallback();
        });

        this.video.addEventListener('error', () => {
          this.showVideoFallback();
        });

        // Ensure video plays when in view
        this.video.addEventListener('canplay', () => {
          if (this.isInViewport()) {
            this.video.play().catch(() => {
              // Auto-play failed, show fallback
              this.showVideoFallback();
            });
          }
        });
      } else if (this.video.tagName === 'IFRAME') {
        // For YouTube/Vimeo iframes
        this.video.addEventListener('load', () => {
          this.video.setAttribute('data-loaded', 'true');
          this.hideVideoFallback();
        });

        this.video.addEventListener('error', () => {
          this.showVideoFallback();
        });
      }
    }

    setupImageLoading() {
      if (!this.images || this.images.length === 0) {
        return;
      }
      
      this.images.forEach(img => {
        if (!img) return;
        
        if (img.complete) {
          img.setAttribute('data-loaded', 'true');
        } else {
          img.addEventListener('load', () => {
            img.setAttribute('data-loaded', 'true');
          });

          img.addEventListener('error', () => {
            img.style.display = 'none';
          });
        }
      });
    }

    setupIntersectionObserver() {
      // Enhanced intersection observer for better performance
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.handleInView();
          } else {
            this.handleOutOfView();
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '50px'
      });

      observer.observe(this);
    }

    handleInView() {
      // Start video if it exists and needs to play
      if (this.video && this.video.tagName === 'VIDEO' && this.video.paused) {
        this.video.play().catch(() => {
          // Auto-play failed, this is expected on some mobile devices
        });
      }

      // Trigger animations if they exist
      this.triggerAnimations();
    }

    handleOutOfView() {
      // Pause video to save bandwidth when out of view
      if (this.video && this.video.tagName === 'VIDEO' && !this.video.paused) {
        this.video.pause();
      }
    }

    triggerAnimations() {
      const animatedElements = this.querySelectorAll('[data-cascade]');
      if (!animatedElements || animatedElements.length === 0) return;
      
      animatedElements.forEach((element, index) => {
        if (!element) return;
        setTimeout(() => {
          element.classList.add('animate');
        }, index * 100); // Stagger animations by 100ms
      });
    }

    hideVideoFallback() {
      if (this.fallbackImage) {
        this.fallbackImage.style.opacity = '0';
        setTimeout(() => {
          this.fallbackImage.style.display = 'none';
        }, 300);
      }
    }

    showVideoFallback() {
      if (this.fallbackImage) {
        this.fallbackImage.style.display = 'block';
        this.fallbackImage.style.opacity = '1';
      }
      
      // Hide video container if it failed
      const videoContainer = this.querySelector('.hero__video-container');
      if (videoContainer) {
        videoContainer.style.display = 'none';
      }
    }

    isInViewport() {
      const rect = this.getBoundingClientRect();
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );
    }
  }

  // Register the custom element
  if (!customElements.get('hero-section')) {
    customElements.define('hero-section', HeroSection);
  }

  // Initialize sections when DOM is ready
  if (!window.heroInitialized) {
    window.heroInitialized = true;
    
    document.addEventListener('DOMContentLoaded', function() {
      const heroSections = document.querySelectorAll('hero-section');
      if (!heroSections) return;
      
      heroSections.forEach(section => {
        if (!section) return;
        // Force initialization for sections that might not have connected yet
        if (!section.initialized) {
          try {
            section.init();
          } catch (error) {
            console.warn('Hero section init error:', error);
          }
        }
      });
    });

    // Handle reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.documentElement.style.setProperty('--animation-duration', '0s');
    }

    // Handle visibility change (for video pausing when tab is hidden)
    document.addEventListener('visibilitychange', () => {
      const heroSections = document.querySelectorAll('hero-section');
      if (!heroSections) return;
      
      heroSections.forEach(hero => {
        if (!hero) return;
        const video = hero.querySelector('.hero__video');
        if (video && video.tagName === 'VIDEO') {
          if (document.hidden) {
            video.pause();
          } else if (hero.isInViewport && hero.isInViewport()) {
            video.play().catch(() => {
              // Auto-play failed
            });
          }
        }
      });
    });

    // Handle window resize for responsive adjustments
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const heroSections = document.querySelectorAll('hero-section');
        if (!heroSections) return;
        
        heroSections.forEach(hero => {
          if (!hero) return;
          // Recalculate video dimensions if needed
          const video = hero.querySelector('.hero__video');
          if (video && video.tagName === 'VIDEO') {
            // Force recalculation of video positioning
            video.style.transform = 'translate(-50%, -50%)';
          }
        });
      }, 250);
    });
  }
}